import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import InboundSlaService from '_services/inbound-sla-service';

export const fetchInboundSla = createAsyncThunk(
  'inboundSla/fetch',
  async (params, { signal }) => {
    return await InboundSlaService.getInboundSla(params, signal);
  },
  {
    // Drop the dispatch if a report is already loading. Reports take 30s+ and
    // run on a single-threaded backend, so stacking duplicate requests (e.g.
    // double-clicks, re-mounts, or the post-patch refetch) only serializes work
    // and inflates latency. A new request must wait for the current one to finish.
    condition: (_params, { getState }) => {
      if (getState().inboundSla.status === 'loading') return false;
    },
  }
);

export const patchInboundOverride = createAsyncThunk(
  'inboundSla/patchOverride',
  async ({ bookingId, bookingIds, kpiKey, pass, passReason, reasonCode, reasonDetail }) => {
    const ids = [...new Set((bookingIds?.length ? bookingIds : [bookingId]).filter(Boolean))];
    const reasonFields = { passReason, reason: passReason, reasonCode, reasonDetail };
    const patch =
      kpiKey
        ? { inboundSlaOverride: { [kpiKey]: { pass, ...reasonFields } } }
        : { inboundSlaOverride: { pass, ...reasonFields } };
    await Promise.all(ids.map((id) => InboundSlaService.patchBooking(id, patch)));
    return { bookingIds: ids };
  }
);

const initialState = {
  data: null,
  status: 'idle',
  error: null,
  patchStatus: 'idle',
};

// Maps the per-KPI override key (sent to the backend) to the daily-report KPI
// object key whose rawData rows the UI renders + tallies from.
const OVERRIDE_KEY_TO_REPORT_KPI = {
  lt230_6: 'lt230SeatsA',
  lt230_11: 'lt230SeatsB',
  lt230_21: 'lt230SeatsC',
  gte230_16: 'gte230SeatsA',
  gte230_21: 'gte230SeatsB',
  gte230_31: 'gte230SeatsC',
};

// Pass-direction cascade, mirroring the backend INBOUND_CASCADE_FROM_KPI: a
// manual PASS on the tighter KPI also passes the looser one for the same
// booking (6→11 min, 16→21 min). Fails do not cascade.
const OVERRIDE_CASCADE_TO_REPORT_KPI = {
  lt230_6: 'lt230SeatsB',
  gte230_16: 'gte230SeatsB',
};

const applyOverrideToRow = (row, pass, passReason) => {
  row.pass = pass;
  row.passReason = passReason;
  row.isManualOverride = true;
};

/**
 * Optimistically applies an override to the loaded report's rawData rows so the
 * summary/scores (which the UI recomputes live from rawData) update instantly,
 * without a full ~30s+ report refetch. Mirrors the backend override + cascade
 * rules. A missing kpiKey means a top-level override that applies to every KPI.
 */
const applyInboundOverrideLocally = (state, { bookingId, bookingIds, kpiKey, pass, passReason } = {}) => {
  const ids = new Set((bookingIds?.length ? bookingIds : [bookingId]).filter(Boolean));
  if (!state.data || !Array.isArray(state.data.dailyReports) || ids.size === 0) return;

  const directKey = kpiKey ? (OVERRIDE_KEY_TO_REPORT_KPI[kpiKey] || kpiKey) : null;
  const cascadeKey = kpiKey && pass === true ? OVERRIDE_CASCADE_TO_REPORT_KPI[kpiKey] : null;

  state.data.dailyReports.forEach((report) => {
    const kpis = report?.kpis;
    if (!kpis) return;

    Object.keys(kpis).forEach((reportKey) => {
      const kpi = kpis[reportKey];
      if (!kpi || !Array.isArray(kpi.rawData)) return;

      const isTarget = !directKey || reportKey === directKey; // null directKey => top-level, all KPIs
      const isCascade = cascadeKey && reportKey === cascadeKey;
      if (!isTarget && !isCascade) return;

      const rowPass = isCascade ? true : pass;
      kpi.rawData.forEach((row) => {
        if (ids.has(row?.bookingId)) applyOverrideToRow(row, rowPass, passReason);
      });
    });
  });
};

const inboundSlaSlice = createSlice({
  name: 'inboundSla',
  initialState,
  reducers: {
    resetInboundSla: (state) => {
      state.data = null;
      state.status = 'idle';
      state.error = null;
    },
    resetPatchInboundOverrideStatus: (state) => {
      state.patchStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInboundSla.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchInboundSla.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchInboundSla.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(patchInboundOverride.pending, (state, action) => {
        state.patchStatus = 'loading';
        // Apply the override to the loaded report immediately; the PATCH below
        // persists it. The summary recomputes from rawData, so no refetch needed.
        applyInboundOverrideLocally(state, action.meta.arg);
      })
      .addCase(patchInboundOverride.fulfilled, (state) => {
        state.patchStatus = 'succeeded';
      })
      .addCase(patchInboundOverride.rejected, (state) => {
        state.patchStatus = 'failed';
      });
  },
});

export const selectInboundSlaData = (state) => state.inboundSla.data;
export const selectInboundSlaStatus = (state) => state.inboundSla.status;
export const selectInboundSlaError = (state) => state.inboundSla.error;
export const selectPatchInboundOverrideStatus = (state) => state.inboundSla.patchStatus;

export const { resetInboundSla, resetPatchInboundOverrideStatus } = inboundSlaSlice.actions;

export default inboundSlaSlice.reducer;
